import { faCalendarDays } from '@fortawesome/pro-regular-svg-icons';
import moment from 'moment';
import * as React from 'react';

import Button from '../../atoms/Button';
import DatePicker from '../DatePicker';
import FilterControl from '../../atoms/FilterControl';
import Overlay from '../../atoms/Overlay';
import Surface from '../../atoms/Surface';
import PresetDateSelector, {
  PresetOption,
  OnChangeDateProps,
} from '../../molecules/PresetDateSelector';

import styles from './DateFilter.module.scss';
import { DateInput } from '../../molecules';

export interface Props
  extends Omit<React.HTMLAttributes<HTMLElement>, 'onSubmit'> {
  className?: string;
  style?: React.CSSProperties;
  isRangePicker?: boolean;
  from: string;
  to: string;
  onSubmit: (from: string, to: string) => void;
  minDate?: Date | undefined;
  maxDate?: Date | undefined;
}

const DateFilter: React.FunctionComponent<Props> = ({
  className,
  style,
  isRangePicker,
  from,
  to,
  onSubmit,
  minDate,
  maxDate,
  ...htmlElementProps
}) => {
  // Define date input parameters
  const inputFormat = 'YYYY-MM-DD';

  // Define states
  const [show, setShow] = React.useState(false);
  const [target, setTarget] = React.useState(null);
  const [preset, setPreset] = React.useState<PresetOption>('');

  // Global date states
  const [startDate, setStartDate] = React.useState(from);
  const [endDate, setEndDate] = React.useState(to);

  // Input date states
  const [inputDate, setInputDate] = React.useState(
    moment(from).format(inputFormat),
  );
  const [inputStartDate, setInputStartDate] = React.useState(
    moment(from).format(inputFormat),
  );
  const [inputEndDate, setInputEndDate] = React.useState(
    moment(to).format(inputFormat),
  );

  /**
   * Handle a date change made from the date picker or a preset date
   */
  const handleIntervalChange = React.useCallback((vars: OnChangeDateProps) => {
    setInputDate(moment(vars.from).format(inputFormat));
    setInputStartDate(moment(vars.from).format(inputFormat));
    setInputEndDate(moment(vars.to).format(inputFormat));
    setStartDate(vars.from);
    setEndDate(vars.to);
    setPreset(vars.presetName);
  }, []);

  /**
   * Sync input values with input states and sync with global states if dates are valid
   */
  const onDateInputChange = React.useCallback(
    ({ name, value }: { name: string; value: string }) => {
      if (name === 'from') {
        const newStartDate = moment(value, inputFormat);
        setInputStartDate(value);
        if (newStartDate.isValid()) {
          setStartDate(newStartDate.toISOString());
        }
      } else if (name === 'to') {
        const newEndDate = moment(value, inputFormat);
        setInputEndDate(value);
        if (newEndDate.isValid()) {
          setEndDate(newEndDate.endOf('day').toISOString());
        }
      } else if (name === 'date') {
        const newDate = moment(value, inputFormat);
        setInputDate(value);
        if (newDate.isValid()) {
          setStartDate(newDate.startOf('day').toISOString());
          setEndDate(newDate.endOf('day').toISOString());
        }
      }
    },
    [],
  );

  /**
   * Ensure input dates are valid and sync states
   */
  const onDateInputBlur = React.useCallback(
    ({ name, value }: { name: string; value: string }) => {
      if (name === 'from') {
        const newStartDate = moment(value, inputFormat);
        if (newStartDate.isValid()) {
          setStartDate(newStartDate.toISOString());
        } else {
          setInputStartDate(moment(startDate).format(inputFormat));
        }
      } else if (name === 'to') {
        const newEndDate = moment(value, inputFormat);
        if (newEndDate.isValid()) {
          setEndDate(newEndDate.endOf('day').toISOString());
        } else {
          setInputEndDate(moment(endDate).format(inputFormat));
        }
      } else if (name === 'date') {
        const newDate = moment(value, inputFormat);
        if (newDate.isValid()) {
          setStartDate(newDate.startOf('day').toISOString());
          setEndDate(newDate.endOf('day').toISOString());
        } else {
          setInputDate(moment(startDate).format(inputFormat));
        }
      }
    },
    [startDate, endDate],
  );

  /**
   * Ensure form can be submitted
   */
  const canSubmit = React.useMemo(
    () => moment(endDate).diff(moment(startDate), 'days') >= 0,
    [startDate, endDate],
  );

  /**
   * Handle submit button click
   */
  const handleSubmit = React.useCallback(() => {
    if (canSubmit) {
      onSubmit(startDate, endDate);
      setShow(false);
    }
  }, [canSubmit, onSubmit, startDate, endDate]);

  /**
   * Handle cancel button click
   */
  const handleCancel = React.useCallback(() => {
    setStartDate(from);
    setEndDate(to);
    setShow(false);
  }, [from, to]);

  /** Sync internal state with external state */
  React.useEffect(() => {
    setStartDate(from);
    setEndDate(to);
    setInputDate(moment(from).format(inputFormat));
    setInputStartDate(moment(from).format(inputFormat));
    setInputEndDate(moment(to).format(inputFormat));
  }, [from, to]);

  return (
    <div className={styles.wrapper} {...htmlElementProps}>
      <FilterControl
        className={className}
        style={style}
        ref={setTarget as React.Ref<HTMLDivElement>}
        prepend={faCalendarDays}
        placeholder={
          isRangePicker
            ? `${moment(from).format('DD/MM/YYYY')} - ${moment(to).format(
                'DD/MM/YYYY',
              )}`
            : moment(from).format('DD/MM/YYYY')
        }
        onClick={() => setShow(!show)}
        readOnly
      />

      <Overlay
        target={target}
        placement="bottom-end"
        show={show}
        onHide={() => setShow(false)}
      >
        <Surface
          className="d-flex flex-column"
          backgroundColor="grayLight3"
          padding="sm"
        >
          <PresetDateSelector
            className="d-flex flex-justify-between"
            presetName={preset}
            display={{ today: false, current: true }}
            onChange={handleIntervalChange}
          />
          <Surface
            backgroundColor="grayLight1"
            padding="none"
            style={{ margin: '1rem 0' }}
          >
            <DatePicker
              isRangePicker={isRangePicker}
              from={startDate}
              to={endDate}
              onChange={handleIntervalChange}
              minDate={minDate}
              maxDate={maxDate}
            />
          </Surface>
          {isRangePicker ? (
            <div className={styles['date-input-wrapper']}>
              <DateInput
                name="from"
                label="Data inicial"
                value={inputStartDate}
                onChange={(value) => onDateInputChange({ name: 'from', value })}
                onBlur={(value) => onDateInputBlur({ name: 'from', value })}
                style={{ width: '194px' }}
              />
              <DateInput
                name="to"
                label="Data final"
                value={inputEndDate}
                onChange={(value) => onDateInputChange({ name: 'to', value })}
                onBlur={(value) => onDateInputBlur({ name: 'to', value })}
                style={{ width: '194px' }}
              />
            </div>
          ) : (
            <div className={styles['date-input-wrapper']}>
              <DateInput
                name="date"
                label="Data"
                value={inputDate}
                onChange={(value) => onDateInputChange({ name: 'date', value })}
                onBlur={(value) => onDateInputBlur({ name: 'date', value })}
                style={{ width: '407px' }}
              />
            </div>
          )}

          <div className="d-flex flex-justify-end">
            <Button variant="link" onClick={handleCancel}>
              Cancelar
            </Button>
            <Button disabled={!canSubmit} onClick={handleSubmit}>
              Aplicar
            </Button>
          </div>
        </Surface>
      </Overlay>
    </div>
  );
};

export default DateFilter;
